import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectMachines, selectMachinesLoaded, getMachines, scheduleProductionOperationOnMachine } from '@reducers/machines';
import { updateJobMachines } from '@reducers/details';
import { Machine } from '@interfaces/machines';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { JobMachinesModalProps } from '@v2/pages/machines/modules/now/components/Job/components/JobMachinesModal';

type MachineItem = Machine & {
  value: number;
  name: string;
};

interface NewMachines {
  linkType: string;
  machineId: number | string;
  machineName: string;
}

export interface UseManageJobMachinesModalParams extends Pick<JobMachinesModalProps, 'job' | 'jobIds' | 'customOnSelect' | 'selectedMachineId'> {}

export const useManageJobMachinesModal = ({
  job,
  jobIds,
  customOnSelect,
  selectedMachineId,
}: UseManageJobMachinesModalParams) => {
  const dispatch = useAppDispatch();
  const machines = useSelector(selectMachines);
  const machinesLoaded = useSelector(selectMachinesLoaded);

  const [submitLoading, setSubmitLoading] = useState(false);
  const initialSelectedMachineId = selectedMachineId ?? job?.machines?.[0]?.machineId ?? null;
  const [selected, setSelected] = useState(initialSelectedMachineId);

  const initiallySelectedMachineIdRef = useRef(initialSelectedMachineId);

  useEffect(() => {
    if (!machinesLoaded) dispatch(getMachines());
  }, [machinesLoaded]);

  const data = useMemo(
    () =>
      machines.map((m) => ({
        ...m,
        name: m.shortName,
        value: m.machineId,
      })),
    [machines]
  );

  const onSelect = (item: MachineItem) => {
    setSelected((prev: number) => (prev === item.value ? null : item.value));
  };

  const onCancel = () => {};

  const onSubmit = async () => {
    if (initiallySelectedMachineIdRef.current === selected) {
      return;
    }
    setSubmitLoading(true);

    await dispatch(scheduleProductionOperationOnMachine({
      productionOperationId: jobIds[0],
      machineId: selected,
    }));

    let newMachines: NewMachines[] = [];

    if (selected !== null) {
      const picked = machines.find((m) => m.machineId === selected);
      newMachines = [
        {
          linkType: 'ScheduledOn',
          machineId: selected,
          machineName: picked?.shortName ?? '',
        },
      ];
      if (picked) {
        customOnSelect?.({ name: picked?.shortName, id: picked?.machineId });
      }
    }

    dispatch(updateJobMachines(jobIds[0], newMachines));

    setSubmitLoading(false);
  };

  return {
    submitLoading,
    data,
    onCancel,
    onSelect,
    onSubmit,
    selected,
  };
};
